import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MessageCircle, Send, X } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function randomSession() {
  return `pub-${Math.random().toString(36).slice(2, 10)}`;
}

export default function AIChat() {
  const [open, setOpen] = useState(false);
  const [sessionId] = useState(() => {
    const saved = localStorage.getItem("cab2go-chat-session");
    if (saved) return saved;
    const fresh = randomSession();
    localStorage.setItem("cab2go-chat-session", fresh);
    return fresh;
  });
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Hi — I'm the Cab2Go local assistant. Ask about fares, service areas, or how to book." },
  ]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const send = async () => {
    const text = draft.trim();
    if (!text || busy) return;
    setDraft("");
    setMessages((old) => [...old, { role: "user", text }]);
    setBusy(true);

    const placeholderIndex = messages.length + 1;
    setMessages((old) => [...old, { role: "assistant", text: "" }]);

    try {
      const response = await fetch(`${BACKEND_URL}/api/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      if (!response.ok || !response.body) throw new Error("network");
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setMessages((old) => {
          const copy = [...old];
          copy[placeholderIndex] = { role: "assistant", text: acc };
          return copy;
        });
      }
      if (!acc.trim()) {
        setMessages((old) => {
          const copy = [...old];
          copy[placeholderIndex] = { role: "assistant", text: "Sorry, I couldn't reach the assistant. Please call 01206 000 000." };
          return copy;
        });
      }
    } catch {
      setMessages((old) => {
        const copy = [...old];
        copy[placeholderIndex] = { role: "assistant", text: "Sorry, I couldn't reach the assistant. Please call 01206 000 000." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {!open && (
        <button
          className="ai-chat-launcher"
          onClick={() => setOpen(true)}
          aria-label="Open Cab2Go assistant"
          data-testid="ai-chat-launcher-button"
        >
          <MessageCircle size={20} />
          <span>Ask Cab2Go</span>
        </button>
      )}
      {open && (
        <div className="ai-chat-panel" role="dialog" aria-label="Cab2Go assistant" data-testid="ai-chat-panel">
          <header className="ai-chat-head">
            <div>
              <span className="eyebrow">Ask Cab2Go</span>
              <strong>Local assistant</strong>
            </div>
            <button
              className="ai-chat-close"
              onClick={() => setOpen(false)}
              aria-label="Close assistant"
              data-testid="ai-chat-close-button"
            >
              <X size={18} />
            </button>
          </header>
          <div className="ai-chat-body" ref={scrollRef} data-testid="ai-chat-messages">
            {messages.map((m, i) => (
              <div
                key={i}
                className={`ai-chat-bubble ai-chat-${m.role}`}
                data-testid={`ai-chat-message-${m.role}-${i}`}
              >
                {m.text || (busy && i === messages.length - 1 ? "…" : "")}
              </div>
            ))}
          </div>
          <div className="ai-chat-input">
            <textarea
              placeholder="Ask about fares, airports, or booking…"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKey}
              rows={2}
              disabled={busy}
              data-testid="ai-chat-input"
            />
            <button
              onClick={send}
              disabled={busy || !draft.trim()}
              aria-label="Send message"
              data-testid="ai-chat-send-button"
            >
              <Send size={16} />
            </button>
          </div>
          <p className="ai-chat-note">Answers are AI-generated. Confirm details with dispatch.</p>
        </div>
      )}
    </>
  );
}
